import React, { useEffect, useState } from 'react';
import EmployeeService from '../AppSettings/services/EmployeeService';
import './HomePage.css';
import useActiveAccount from '../AppSettings/hooks/useActiveAccount';
import useCurrentEmployee from '../AppSettings/hooks/useCurrentEmployee';
// New dashboard widgets
import ActiveWorkloadWidget from '@frontend/Components/ActiveWorkloadWidget';
import ActionItemsWidget from '@frontend/Components/ActionItemsWidget';
import FinancialSnapshotWidget from '@frontend/Components/FinancialSnapshotWidget';
import CRMActivityWidget from '@frontend/Components/CRMActivityWidget';
import WelcomeBanner from '@frontend/Components/WelcomeBanner';

const HomePage: React.FC = () => {
  const account = useActiveAccount();
  const currentEmployee = useCurrentEmployee();
  const permishId = currentEmployee?.permish_id ?? null;
  const permishIdNum = typeof permishId === 'string' ? parseInt(permishId, 10) : permishId;
  const showFinancialSnapshot = permishIdNum !== 2 && permishIdNum !== 3;
  const showCRMActivity = permishIdNum !== 2 && permishIdNum !== 3;
  // Build C (plan field-members-and-signin-conversion): an onboarding refusal — most
  // importantly the field-member email guard's 409 ("ask an administrator to enable
  // sign-in") — must be VISIBLE; a swallowed refusal leaves the person with no row and no
  // idea why nothing works.
  const [onboardingError, setOnboardingError] = useState<string | null>(null);

  useEffect(() => {
    // A stale refusal must not outlive its cause (pre-PR review): clear on every run, and
    // GUARD every write after an await — without the guard, a slow run for the previous
    // account could repaint its refusal (or POST its onboarding) under the next account.
    let isCurrent = true;
    const checkCurrentUserDetails = async () => {
      setOnboardingError(null);
      if (!account) {
        return;
      }

      try {
        const { idTokenClaims, name, username } = account;
        const oid = idTokenClaims?.oid as string | undefined;

        if (!oid) {
          console.warn('Active account is missing an object ID.');
          return;
        }

        // Check if user exists via service
        const data = await EmployeeService.checkUser(oid);
        if (!isCurrent) {
          return;
        }

        // Create new employee if they don't exist
        if (!data.exists) {
          const [firstName = '', lastName = ''] = (name || '').split(' ');
          try {
            await EmployeeService.createEmployee({
              employeeId: oid,
              email: username ?? '',
              firstName,
              lastName,
            });
          } catch (createError) {
            if (!isCurrent) {
              return;
            }
            // The refusal names the path (e.g. "…already exists as a field member — ask an
            // administrator to enable sign-in"). Show it; do not swallow it.
            setOnboardingError(
              (createError as Error)?.message
                || 'Your account could not be set up. Contact an administrator.',
            );
            return;
          }
        }
        if (isCurrent) {
          setOnboardingError(null);
        }
      } catch (error) {
        console.log('Error checking current user details:', error);
      }
    };

    checkCurrentUserDetails();
    return () => {
      isCurrent = false;
    };
  }, [account]);

  /**
   * Render the dashboard layout
   *
   * Layout Structure:
   * - Top Section: Welcome banner (to be added)
   * - Middle Section (Two Columns):
   *   - Left: ActiveWorkloadWidget (donut chart)
   *   - Right: ActionItemsWidget (bar chart + action items list)
   * - Bottom Section (Two Columns):
   *   - Left: FinancialSnapshotWidget (bar chart + summary cards)
   *   - Right: CRMActivityWidget (client stats + recent contacts)
   *
   * Legacy widgets are kept for backward compatibility but can be removed
   * once the new dashboard is fully tested and approved.
   */
  return (
    <div className="HP-Container">
      {onboardingError && (
        <div className="HP-onboarding-error" role="alert">
          {onboardingError}
        </div>
      )}
      {/* Welcome Banner */}
      <WelcomeBanner />

      {/* New Dashboard Layout */}
      <div className="HP-Dashboard-Grid">
        {/* Top Row - Two Columns */}
        <div className="HP-Dashboard-Row">
          <div className="HP-Dashboard-Column">
            <ActiveWorkloadWidget />
          </div>
          <div className="HP-Dashboard-Column">
            <ActionItemsWidget />
          </div>
        </div>

        {/* Bottom Row - Two Columns */}
        <div className="HP-Dashboard-Row">
          {showFinancialSnapshot && (
            <div className="HP-Dashboard-Column">
              <FinancialSnapshotWidget />
            </div>
          )}
          {showCRMActivity && (
            <div className="HP-Dashboard-Column">
              <CRMActivityWidget />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomePage;
